import { defineComponent, onMounted, reactive, toRefs, watch } from "vue";
import { Bar } from "vue-chartjs";
import { Chart as ChartJS, registerables, CategoryScale, LinearScale, Title, Tooltip, Legend, BarElement } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

import { BarDataset, BarProp } from "./interfaces";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ...registerables, ChartDataLabels);
export default defineComponent({
    name: 'BarComponent',
    props: {
        style: { type: String, default: 'NORMAL' },
        labels: { type: Array as () => string[], required: true },
        datasets: { type: Array as () => BarDataset[], required: true },
        isStacked: {
            type: Boolean,
            default: false
        },
        indexAxis: {
            type: String,
            default: 'x'
        },
        unit: {
            type: String,
            default: ''
        }
    },
    components: {
        Bar
    },
    setup(props: BarProp, { emit }) {
        const config = reactive({
            loaded: false,
            key: 0
        });

        const barOpts = {
            responsive: true,
            maintainAspectRatio: false,
            indexAxis: props.indexAxis,
            x: {
                stacked: props.isStacked
            },
            y: {
                stacked: props.isStacked,
                beginAtZero: true,
                ticks: {
                    callback: function (value) {
                        return parseFloat(value).toFixed(2);
                    }
                }
            },
            plugins: {
                zoom: {
                    pan: {
                        enabled: true,
                        mode: 'x',
                    },
                    zoom: {
                        wheel: {
                            enabled: true,
                        },
                        pinch: {
                            enabled: true
                        },
                        mode: 'x',
                    }
                },
                tooltip: {
                    usePointStyle: true,
                    callbacks: {
                        label: function (context) {
                            const value = context.raw
                            const unit = context.dataset.unit ?? ''
                            return (unit === "h") ? `${context.dataset.label}: ${parseFloat(value).toFixed(2)}${unit}` : value
                        }
                    }
                },
                 datalabels: {                  
                    formatter: (value, context) => {                        
                        const unit = context.dataset.unit ?? ''                        
                        return (unit === "h") ? `${parseFloat(value).toFixed(2)}${unit}`: value;
                    },
                     
                }
            },

        }

        const barHorizontalOpts = {
            responsive: true,
            maintainAspectRatio: false,
            x: {
                stacked: props.isStacked
            },
            y: {
                stacked: props.isStacked,
                beginAtZero: true,
                ticks: {
                    callback: function (value) {
                        return parseFloat(value).toFixed(2);
                    }
                }
            },
            plugins: {
                zoom: {
                    pan: {
                        enabled: true,
                        mode: 'x',
                    },
                    zoom: {
                        wheel: {
                            enabled: true,
                        },
                        pinch: {
                            enabled: true
                        },
                        mode: 'x',
                    }
                },
                tooltip: {
                    usePointStyle: true,
                     callbacks: {
                        label: function (context) {    
                            const value = context.raw
                            const unit = context.dataset.unit ?? ''
                            return `${context.dataset.label}: ${parseFloat(value).toFixed(2)}${unit}`
                        }
                    }
                }
            }
        }

        const barHorizontal_Percent = {
            responsive: true,
            maintainAspectRatio: false,
            indexAxis: 'y',
            scales: {
                x: {
                    beginAtZero: true,
                    max: 100,
                    ticks: {
                        callback: (value) => `${value}%`
                    }
                },
                y: {
                    beginAtZero: true,
                }
            },
            plugins: {
                tooltip: {
                    callbacks: {
                        label: function (context) {    
                            const value = context.raw
                            const unit = context.dataset.unit ?? ''
                            return `${context.dataset.label}: ${parseFloat(value).toFixed(2)}${unit}`
                        }
                    }
                },
                legend: { display: false },
                datalabels: {
                    color: function (context) {
                        return context.dataset.data[context.dataIndex] < 10 ? '#333' : '#333';
                    },
                    anchor: function (context) {
                        return context.dataset.data[context.dataIndex] < 10 ? 'end' : 'center';
                    },
                    align: function (context) {
                        return context.dataset.data[context.dataIndex] < 10 ? 'right' : 'center';
                    },
                    formatter: (value, context) => {
                        const unit = context.dataset.unit ?? ''                        
                         return  `${parseFloat(value).toFixed(2)}${unit}`;
                    },
                    font: {
                        weight: 'bold',
                        size: 12
                    },
                    clamp: true 
                }
            }
};


        const getConfigBar = (style: string) => {
            switch (style) {
                case 'NORMAL':
                    return barOpts;
                case 'HORIZONTAL':
                    return barHorizontalOpts;
                case 'HORIZONTAL_PERCENTAGE':
                    return barHorizontal_Percent;
                default:
                    return barDataConfig.barConfig = barOpts;
            }
        }
        
        const barDataConfig = reactive({

            barData: {
                labels: [] as string[],
                datasets: [] as BarDataset[],
            },
            barConfig: getConfigBar(props.style),


        });

        onMounted(() => {
            if (props.labels.length > 0) {
                barDataConfig.barData.labels = props.labels;
                barDataConfig.barData.datasets = props.datasets;
                config.key++;
                config.loaded = true;
            }
        })

        watch([() => props.labels, () => props.datasets, () => props.style], (
            [newLabels, newData, newStyle], [oldLabels, oldData, oldStyle]
        ) => {
            if (newLabels != oldLabels) {
                barDataConfig.barData.labels = newLabels;
                barDataConfig.barData.datasets = newData;
                config.key++;
                config.loaded = true;
            }

            if (newStyle != oldStyle) {
                barDataConfig.barConfig = getConfigBar(newStyle);
            }
        });

        return {
            ...toRefs(config),
            ...toRefs(barDataConfig),
            barOpts
        }
    }
});